import React, { memo, useEffect, useState, useRef, PropsWithChildren, FC } from 'react';

import { CachedImages } from '@gem/call/Removable/stores/CachedImages/CachedImages';
import { preloadImage } from '@gem/call/Removable/tasks/preloadImage/preloadImage';
import { RemoteImage } from '@gem/modules/remote-storage';

interface IAsyncImageProps {
  className?: string;
  src: string;
  alt: string;
  dataTest?: string;
}

export const AsyncImage: FC<PropsWithChildren<IAsyncImageProps>> = memo((props) => {
  const { className, src, alt, children, dataTest } = props;
  const prevImageSrcRef = useRef('');

  const [isLoaded, setLoaded] = useState<boolean | null>(() => {
    return CachedImages.isCachedImage(src);
  });

  useEffect(() => {
    let isMounted = true;
    const loadImage = async (): Promise<void> => {
      if (!CachedImages.isCachedImage(src)) {
        const result = await preloadImage(src);
        if (isMounted) {
          prevImageSrcRef.current = result ? src : '';
          setLoaded(result);
        }
        return;
      }

      setLoaded(true);
      prevImageSrcRef.current = src;
    };

    loadImage();

    return (): void => {
      isMounted = false;
      setLoaded(null);
    };
  }, [src]);

  if (isLoaded || prevImageSrcRef.current) {
    return (
      <RemoteImage
        alt={alt}
        className={className}
        src={src || prevImageSrcRef.current}
        data-test={dataTest}
      />
    );
  }

  return <>{children}</>;
});
